import { useState } from "react";
import { ShieldAlert, ShieldCheck, XCircle } from "lucide-react";
import { useDevAgentStore, type FeedItem as FeedItemT } from "@/stores/devAgentStore";
import { approveDevAgentAction } from "../../devAgentService";
import { cn } from "@/lib/utils";

export function ApprovalInlineCard({
  item,
  onSendMessage,
}: {
  item: FeedItemT;
  onSendMessage?: (msg: string) => void;
}) {
  const [acted, setActed] = useState(false);
  const [result, setResult] = useState<"approved" | "rejected" | null>(null);
  const disabled =
    useDevAgentStore((s) => s.runStatus === "running" || !!s.streamingContent);
  const d = item.data;

  const handleAction = async (approved: boolean) => {
    if (acted || disabled) return;
    setActed(true);
    setResult(approved ? "approved" : "rejected");
    const uuid = useDevAgentStore.getState().sessionUuid;
    if (uuid) {
      try {
        await approveDevAgentAction(uuid, {
          approved,
          approval_id: d.approvalId || "",
        });
        useDevAgentStore.getState().setPendingApproval(null);
      } catch {
        /* toast handled by caller */
      }
    }
    onSendMessage?.(approved ? "Approved." : "Rejected.");
  };

  return (
    <div className="py-1.5">
      <div
        className={cn(
          "p-3 rounded-xl animate-in fade-in border max-w-[85%]",
          "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30",
        )}
      >
        <p className="text-xs font-bold mb-2 uppercase tracking-tight flex items-center gap-1.5 text-blue-700 dark:text-blue-300">
          <ShieldAlert className="h-3.5 w-3.5" />
          Approval Required
        </p>
        <p className="text-sm mb-3">
          {d.description || d.message || "Do you want to proceed?"}
        </p>

        {Array.isArray(d.changesPreview) && d.changesPreview.length > 0 && (
          <div className="mb-3 space-y-1">
            {d.changesPreview.map((c: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span
                  className={cn(
                    "px-1.5 py-0.5 rounded text-[10px] font-medium",
                    c.action === "create"
                      ? "bg-green-500/15 text-green-400"
                      : c.action === "delete"
                        ? "bg-red-500/15 text-red-400"
                        : "bg-yellow-500/15 text-yellow-400",
                  )}
                >
                  {c.action || c.type}
                </span>
                <span className="truncate text-muted-foreground">{c.file}</span>
              </div>
            ))}
          </div>
        )}

        {!acted ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleAction(true)}
              disabled={disabled}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Approve</span>
            </button>
            <button
              type="button"
              onClick={() => handleAction(false)}
              disabled={disabled}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-background text-foreground hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-3.5 w-3.5" />
              <span>Reject</span>
            </button>
          </div>
        ) : (
          <div
            className={cn(
              "text-xs font-medium py-1",
              result === "approved" ? "text-green-400" : "text-red-400",
            )}
          >
            {result === "approved" ? "\u2713 Approved" : "\u2717 Rejected"}
          </div>
        )}
      </div>
    </div>
  );
}
